/**
 * Enum values shared by the API and the UI. The Prisma schema defines the same
 * values; keep both in sync when adding a value.
 */

export const ROLE_CODES = ['ADMIN', 'ACCOUNTANT', 'VIEWER'] as const;
export type RoleCode = (typeof ROLE_CODES)[number];

export const RECORD_STATUSES = ['ACTIVE', 'INACTIVE'] as const;
export type RecordStatus = (typeof RECORD_STATUSES)[number];

/**
 * Apps (REMOVED = taken down from the store). Anything but ACTIVE behaves like
 * INACTIVE: history and data are kept, the record is not newly assigned and
 * not expected in missing-data checks.
 */
export const APP_STATUSES = ['ACTIVE', 'INACTIVE', 'SUSPENDED', 'REMOVED'] as const;
export type AppStatus = (typeof APP_STATUSES)[number];

/** Ads, AdMob / AdX and Play developer accounts (same rules as apps). */
export const ACCOUNT_STATUSES = ['ACTIVE', 'INACTIVE', 'SUSPENDED'] as const;
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];

export const USER_STATUSES = ['ACTIVE', 'DISABLED'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const APP_PLATFORMS = ['ANDROID', 'IOS', 'WEB', 'OTHER'] as const;
export type AppPlatform = (typeof APP_PLATFORMS)[number];

export const REVENUE_SOURCE_TYPES = ['AD_NETWORK', 'STORE', 'OTHER'] as const;
export type RevenueSourceType = (typeof REVENUE_SOURCE_TYPES)[number];

export const ITC_STATUSES = [
  'ELIGIBLE',
  'PENDING_VERIFICATION',
  'CLAIMED',
  'NOT_ELIGIBLE',
  'REVERSED',
] as const;
export type ItcStatus = (typeof ITC_STATUSES)[number];

export const CLAIM_PERIOD_STATUSES = ['OPEN', 'SUBMITTED', 'CLAIMED', 'CLOSED'] as const;
export type ClaimPeriodStatus = (typeof CLAIM_PERIOD_STATUSES)[number];

export const BILLING_TYPES = ['PREPAID', 'POSTPAID'] as const;
export type BillingType = (typeof BILLING_TYPES)[number];

export const BALANCE_BASES = ['INCLUDING_GST', 'EXCLUDING_GST'] as const;
export type BalanceBasis = (typeof BALANCE_BASES)[number];

/**
 * ITC statuses offered in the normal UI. New marketing GST is ELIGIBLE
 * automatically; PENDING_VERIFICATION and REVERSED remain valid database values
 * for compatibility but are not offered in normal workflows.
 */
export const EDITABLE_ITC_STATUSES = ['ELIGIBLE', 'CLAIMED', 'NOT_ELIGIBLE'] as const;

/** Indian bank account types for payout accounts (EEFC = foreign-currency account). */
export const BANK_ACCOUNT_TYPES = ['SAVINGS', 'CURRENT', 'EEFC', 'OTHER'] as const;
export type BankAccountType = (typeof BANK_ACCOUNT_TYPES)[number];

/** Google Play Console developer account type. */
export const PLAY_ACCOUNT_TYPES = ['PERSONAL', 'ORGANIZATION'] as const;
export type PlayAccountType = (typeof PLAY_ACCOUNT_TYPES)[number];

/** Play Console identity / phone verification state. */
export const PLAY_VERIFICATION_STATUSES = [
  'NOT_STARTED',
  'PENDING',
  'VERIFIED',
  'REJECTED',
] as const;
export type PlayVerificationStatus = (typeof PLAY_VERIFICATION_STATUSES)[number];

export const ASSIGNMENT_STATUSES = ['ACTIVE', 'REMOVED'] as const;
export type AssignmentStatus = (typeof ASSIGNMENT_STATUSES)[number];

/** How advertising accounts are shown in morning entry, dashboards and lists. */
export const ACCOUNT_DISPLAY_MODES = ['ACCOUNT_ID', 'ACCOUNT_NAME', 'BOTH'] as const;
export type AccountDisplayMode = (typeof ACCOUNT_DISPLAY_MODES)[number];

export const EXCHANGE_RATE_SOURCES = ['MONTHLY', 'MANUAL'] as const;
export type ExchangeRateSource = (typeof EXCHANGE_RATE_SOURCES)[number];

export const GST_REGISTRATION_TYPES = [
  'REGULAR',
  'COMPOSITION',
  'SEZ',
  'NON_RESIDENT',
  'OTHER',
] as const;
export type GstRegistrationType = (typeof GST_REGISTRATION_TYPES)[number];

export const AUDIT_ACTIONS = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'RESTORE',
  'RECALCULATE',
  'STATUS_CHANGE',
  'LOGIN',
  'LOGOUT',
  'APP_ACCOUNT_ASSIGNED',
  'APP_ACCOUNT_REMOVED',
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const LABELS = {
  role: { ADMIN: 'Admin', ACCOUNTANT: 'Accountant', VIEWER: 'Viewer' },
  recordStatus: { ACTIVE: 'Active', INACTIVE: 'Inactive' },
  appStatus: {
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
    SUSPENDED: 'Suspended',
    REMOVED: 'Removed',
  },
  accountStatus: { ACTIVE: 'Active', INACTIVE: 'Inactive', SUSPENDED: 'Suspended' },
  userStatus: { ACTIVE: 'Active', DISABLED: 'Disabled' },
  appPlatform: { ANDROID: 'Android', IOS: 'iOS', WEB: 'Web', OTHER: 'Other' },
  revenueSourceType: { AD_NETWORK: 'Ad network', STORE: 'Store', OTHER: 'Other' },
  itcStatus: {
    ELIGIBLE: 'Eligible',
    PENDING_VERIFICATION: 'Pending verification',
    CLAIMED: 'Claimed',
    NOT_ELIGIBLE: 'Not eligible',
    REVERSED: 'Reversed',
  },
  claimPeriodStatus: { OPEN: 'Open', SUBMITTED: 'Submitted', CLAIMED: 'Claimed', CLOSED: 'Closed' },
  billingType: { PREPAID: 'Prepaid', POSTPAID: 'Postpaid (credit line)' },
  balanceBasis: { INCLUDING_GST: 'Including GST', EXCLUDING_GST: 'Excluding GST' },
  accountDisplayMode: {
    ACCOUNT_ID: 'Account ID',
    ACCOUNT_NAME: 'Account name',
    BOTH: 'Account name and ID',
  },
  assignmentStatus: { ACTIVE: 'Active', REMOVED: 'Removed' },
  bankAccountType: {
    SAVINGS: 'Savings',
    CURRENT: 'Current',
    EEFC: 'EEFC (foreign currency)',
    OTHER: 'Other',
  },
  playAccountType: { PERSONAL: 'Personal', ORGANIZATION: 'Organization' },
  playVerificationStatus: {
    NOT_STARTED: 'Not started',
    PENDING: 'Pending',
    VERIFIED: 'Verified',
    REJECTED: 'Rejected',
  },
  gstRegistrationType: {
    REGULAR: 'Regular',
    COMPOSITION: 'Composition',
    SEZ: 'SEZ',
    NON_RESIDENT: 'Non-resident',
    OTHER: 'Other',
  },
} as const;
